import { useEffect, useState } from "react";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import { DaysToggle, Skeleton } from "@/components/Shared";
import { Badge } from "@/components/ui/badge";
import { Package } from "lucide-react";

const STATUS_STYLE = {
  "Healthy": "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400",
  "Low Stock": "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400",
  "Reorder Now": "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400",
};

export default function Products() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/dashboard/products?days=${days}`).then(({ data }) => setData(data));
  }, [days]);

  if (!data) return <div className="space-y-6"><Skeleton className="h-80" /><Skeleton className="h-80" /></div>;

  return (
    <div className="space-y-8" data-testid="products-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground">
          Live Amazon.in top sellers and FBA inventory.
          {data.items_pending > 0 && ` Item details for ${data.items_pending} orders are still syncing — numbers grow as sync completes.`}
        </p>
        <DaysToggle value={days} onChange={setDays} />
      </div>
      <section className="bg-card border border-border rounded-lg shadow-sm overflow-hidden" data-testid="top-sellers-table">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="font-heading text-base font-semibold tracking-tight">Top sellers · last {days} days</h3>
        </div>
        {data.top.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">No item-level data yet — it appears automatically as the order-items sync progresses.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                {["#", "Product", "SKU / ASIN", "Units", "Orders", "Revenue"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.top.map((p, i) => (
                <tr key={p.sku} className="border-t border-border hover:bg-muted/40 transition-colors">
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{i + 1}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <Package className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
                      <p className="font-medium max-w-md truncate">{p.name || p.sku}</p>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{p.sku}<br />{p.asin}</td>
                  <td className="px-4 py-2.5 font-semibold">{p.units}</td>
                  <td className="px-4 py-2.5">{p.orders}</td>
                  <td className="px-4 py-2.5 font-semibold">{inr(p.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      <section className="bg-card border border-border rounded-lg shadow-sm overflow-hidden" data-testid="inventory-table">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="font-heading text-base font-semibold tracking-tight">FBA Inventory (Amazon fulfilment centres)</h3>
        </div>
        {data.inventory.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">No FBA inventory found yet — run a sync from the Integrations page, or your account may not use FBA.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                {["Product", "SKU", "Fulfillable", "Inbound", "Total", "Status"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.inventory.map((p) => (
                <tr key={p.sku} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`inventory-row-${p.sku}`}>
                  <td className="px-4 py-2.5 font-medium max-w-md truncate">{p.name || p.sku}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{p.sku}</td>
                  <td className="px-4 py-2.5">{p.fulfillable}</td>
                  <td className="px-4 py-2.5">{p.inbound}</td>
                  <td className="px-4 py-2.5 font-semibold">{p.total}</td>
                  <td className="px-4 py-2.5">
                    <Badge className={`${STATUS_STYLE[p.status]} border-0 font-semibold`}>{p.status}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
